import { type FC, useEffect, useRef } from "react";
import { useBlocNext } from "@blac/react";
import translate from "src/lib/translate";
import IconPaperclip from "@9amhealth/shared/icons/IconPaperclip";
import { useActionSheet } from "@9amhealth/shared/src/molecule/ActionSheet";
import { TourGuideAnchor } from "@9amhealth/shared";
import { ChatTourAnchor } from "./chatTourConstants";
import { DocumentIcon } from "@9amhealth/shared";
import ChatComposerCubit from "./state/ChatComposerCubit";
import type { LeapChatFileRef } from "./state/types";
import {
  ComposerRow,
  AddAttachmentButton,
  HeroInputCardWrapper,
  HeroInputCard,
  HeroInput,
  SendButton,
  SendButtonSpinner,
  AttachmentPreviewRow,
  AttachmentPreviewItem,
  AttachmentFileIcon,
  AttachmentRemoveButton,
  AttachmentSpinnerOverlay,
  AttachmentErrorOverlay
} from "./leapChat.styles";

const ArrowUpIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="15"
    fill="none"
    viewBox="0 0 13 15"
  >
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M6.083 14.083V.75m0 0L.75 6.083M6.083.75l5.334 5.333"
    />
  </svg>
);

const CloseIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

interface ChatComposerProps {
  onSubmit: (
    text: string,
    fileIds?: string[],
    files?: LeapChatFileRef[]
  ) => void;
  isRunning: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}

const ChatComposer: FC<ChatComposerProps> = ({
  onSubmit,
  isRunning,
  placeholder = translate("leapChat.placeholder.askAnything"),
  autoFocus = false
}) => {
  const [{ isDragging, inputValue, attachments }, cubit] =
    useBlocNext(ChatComposerCubit);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const iosFileInputRef = useRef<HTMLInputElement>(null);

  const { show: showActionSheet } = useActionSheet({
    buttons: cubit.actionSheetButtons,
    onSelect: cubit.handleActionSheetSelect
  });

  useEffect(() => {
    cubit.setCallbacks({ onSubmit, showActionSheet });
  }, [cubit, onSubmit, showActionSheet]);

  useEffect(() => {
    cubit.setIsRunning(isRunning);
  }, [cubit, isRunning]);

  useEffect(() => {
    cubit.setElements({
      textarea: textareaRef.current,
      cameraInput: cameraInputRef.current,
      galleryInput: galleryInputRef.current,
      pdfInput: pdfInputRef.current,
      iosFileInput: iosFileInputRef.current
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cubit,
    textareaRef.current,
    cameraInputRef.current,
    galleryInputRef.current,
    pdfInputRef.current,
    iosFileInputRef.current
  ]);

  useEffect(() => {
    cubit.init();
  }, [cubit]);

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!inputValue) {
      cubit.resetTextareaHeight();
    }
  }, [inputValue, cubit]);

  const hasContent =
    inputValue.trim().length > 0 ||
    attachments.some((a) => a.status === "done");

  return (
    <ComposerRow className="chat-composer">
      <AddAttachmentButton
        type="button"
        aria-label={translate("leapChat.aria.addAttachment")}
        onClick={cubit.handleAttachmentPress}
      >
        <IconPaperclip />
      </AddAttachmentButton>
      <HeroInputCardWrapper>
        <TourGuideAnchor name={ChatTourAnchor.COMPOSER}>
          <HeroInputCard
            style={
              isDragging
                ? { outline: "2px dashed #4a90d9", outlineOffset: "-2px" }
                : undefined
            }
          >
            {attachments.length > 0 && (
              <AttachmentPreviewRow>
                {attachments.map((a) => (
                  <AttachmentPreviewItem key={a.key}>
                    {a.previewUrl ? (
                      <img src={a.previewUrl} alt={a.name} />
                    ) : (
                      <AttachmentFileIcon>
                        <DocumentIcon />
                      </AttachmentFileIcon>
                    )}
                    {a.status === "uploading" && <AttachmentSpinnerOverlay />}
                    {a.status === "error" && <AttachmentErrorOverlay />}
                    <AttachmentRemoveButton
                      type="button"
                      aria-label={translate("leapChat.aria.removeAttachment", {
                        name: a.name
                      })}
                      onClick={() => cubit.removeAttachment(a.key)}
                    >
                      <CloseIcon />
                    </AttachmentRemoveButton>
                  </AttachmentPreviewItem>
                ))}
              </AttachmentPreviewRow>
            )}
            <HeroInput
              ref={textareaRef}
              rows={1}
              value={inputValue}
              onChange={cubit.handleInputChange}
              onKeyDown={cubit.handleKeyDown}
              onPaste={cubit.handlePaste}
              enterKeyHint={cubit.isTouchDevice ? "enter" : "send"}
              placeholder={placeholder}
              id="chat-composer-input"
            />
            {hasContent && !isRunning && (
              <SendButton
                type="button"
                onClick={cubit.submitInput}
                disabled={attachments.some(
                  (a) => a.status === "uploading" || a.status === "error"
                )}
                aria-label={translate("leapChat.aria.sendMessage")}
              >
                <ArrowUpIcon />
              </SendButton>
            )}
            {isRunning && <SendButtonSpinner />}
          </HeroInputCard>
        </TourGuideAnchor>
      </HeroInputCardWrapper>
      {!cubit.isIOS && (
        <>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={cubit.handleImageInput}
            style={{ display: "none" }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            multiple
            onChange={cubit.handleImageInput}
            style={{ display: "none" }}
          />
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            onChange={cubit.handlePdfInput}
            style={{ display: "none" }}
          />
        </>
      )}
      {cubit.isIOS && (
        <input
          ref={iosFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf"
          multiple
          onChange={cubit.handleCombinedInput}
          style={{ display: "none" }}
        />
      )}
    </ComposerRow>
  );
};

export default ChatComposer;
